import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../App";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar } from "./ui/calendar";
import { toast } from "sonner";
import { CalendarIcon, Plus, Send, Info, AlertTriangle, Flag, Users, AlertCircle, ChevronDown, ChevronUp, X } from "lucide-react";
import { format, addDays, parseISO, isWithinInterval, eachDayOfInterval, isWeekend } from "date-fns";
import { fr } from "date-fns/locale";

const MiniAbsenceTimeline = ({ startDate, endDate, conflicts }) => {
  if (!startDate || !endDate) return null;
  const viewStart = addDays(startDate, -1);
  const viewEnd = addDays(endDate, 1);
  const days = eachDayOfInterval({ start: viewStart, end: viewEnd });

  const isInPeriod = (day, periodStart, periodEnd) => {
    const pStart = typeof periodStart === 'string' ? parseISO(periodStart) : periodStart;
    const pEnd = typeof periodEnd === 'string' ? parseISO(periodEnd) : periodEnd;
    return day >= pStart && day <= pEnd;
  };

  const colors = [
    { bg: 'bg-red-400' },
    { bg: 'bg-purple-400' },
    { bg: 'bg-amber-500' },
    { bg: 'bg-pink-400' },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center text-[10px] text-gray-400">
        <div className="w-20 flex-shrink-0"></div>
        <div className="flex-1 flex">
          {days.map((day, i) => (
            <div key={i} className={`flex-1 text-center ${isWeekend(day) ? 'text-gray-300' : ''}`}>
              {format(day, 'dd')}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center">
        <div className="w-20 flex-shrink-0 text-xs text-gray-600 truncate">Vous</div>
        <div className="flex-1 flex h-5 gap-px">
          {days.map((day, i) => {
            const isInRequest = isInPeriod(day, startDate, endDate);
            return (
              <div key={i} className={`flex-1 rounded-sm ${isInRequest ? 'bg-[#00A3E0]' : isWeekend(day) ? 'bg-gray-100' : 'bg-gray-50'}`} />
            );
          })}
        </div>
      </div>
      {conflicts.map((conflict, idx) => {
        const color = colors[idx % colors.length];
        return (
          <div key={idx} className="flex items-center">
            <div className="w-20 flex-shrink-0 text-xs text-gray-600 truncate">
              {conflict.user_name.split(' ')[0]}
            </div>
            <div className="flex-1 flex h-5 gap-px">
              {days.map((day, i) => {
                const isInConflict = isInPeriod(day, conflict.start_date, conflict.end_date);
                return (
                  <div key={i} className={`flex-1 rounded-sm ${isInConflict ? color.bg : isWeekend(day) ? 'bg-gray-100' : 'bg-gray-50'}`} />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Inline date picker — always inline, no popover
const InlineDatePicker = ({ label, selected, onSelect, disabled, holidays, testId }) => {
  const [expanded, setExpanded] = useState(false);

  const isHoliday = useCallback((date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return holidays.find(h => h.date === dateStr);
  }, [holidays]);

  const modifiers = { holiday: (date) => !!isHoliday(date) };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between h-11 px-3 rounded-md border border-input bg-transparent text-sm"
        data-testid={testId}
      >
        <span className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-gray-500" />
          {selected ? format(selected, "dd MMM yyyy", { locale: fr }) : "Sélectionner"}
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
      </button>
      {expanded && (
        <div className="border rounded-lg p-1 bg-white">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(date) => {
              onSelect(date);
              setExpanded(false);
            }}
            locale={fr}
            defaultMonth={selected || undefined}
            disabled={disabled}
            modifiers={modifiers}
          />
          <div className="p-2 border-t text-xs text-gray-500 flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-amber-100 border border-amber-300"></span>
            <span>Jour férié</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Inline leave type picker — expandable list, no native <select>
const InlineTypePicker = ({ leaveTypes, selected, onSelect, selectedType }) => {
  const [expanded, setExpanded] = useState(false);
  const selectedName = leaveTypes.find(t => t.id === selected)?.name;

  return (
    <div className="space-y-2">
      <Label>Type de congé *</Label>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between h-12 px-3 rounded-lg border border-gray-200 bg-white text-left"
        style={{ fontSize: '16px' }}
        data-testid="leave-type-select"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selectedName || 'Sélectionner le type'}
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />}
      </button>
      {expanded && (
        <div className="border rounded-lg bg-white max-h-60 overflow-y-auto">
          {leaveTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => { onSelect(type.id); setExpanded(false); }}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 transition-colors ${selected === type.id ? 'bg-[#00A3E0]/10 text-[#00A3E0]' : 'hover:bg-gray-50'}`}
            >
              <span className="font-medium text-sm">{type.name}</span>
              {type.max_days && <span className="text-xs text-gray-400 ml-2">({type.max_days}j max)</span>}
            </button>
          ))}
        </div>
      )}
      {selectedType && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${selectedType.max_days ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
          <Info className={`h-4 w-4 mt-0.5 flex-shrink-0 ${selectedType.max_days ? 'text-blue-500' : 'text-gray-400'}`} />
          <div>
            <p className={selectedType.max_days ? 'text-blue-700' : 'text-gray-600'}>{selectedType.description}</p>
            {selectedType.max_days && (
              <p className="text-blue-600 font-medium mt-1">Durée maximale : {selectedType.max_days} jour{selectedType.max_days > 1 ? 's' : ''}</p>
            )}
            {!selectedType.paid && (
              <p className="text-amber-600 font-medium mt-1">Non rémunéré</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Pill toggle button for half day
const HalfDayToggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
      checked
        ? 'bg-[#00A3E0] text-white border-[#00A3E0] shadow-sm'
        : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
    }`}
  >
    <span className={`inline-block w-2.5 h-2.5 rounded-full transition-colors ${checked ? 'bg-white' : 'bg-gray-300'}`} />
    Demi-journée
  </button>
);

// Form content
const LeaveFormContent = ({ formData, setFormData, leaveTypes, holidays, selectedType, setSelectedType, daysCount, excludedHolidays, conflictingLeaves, isOverLimit, loading, handleSubmit, onClose }) => {
  const handleTypeChange = (value) => {
    setFormData(prev => ({ ...prev, leave_type: value }));
    const type = leaveTypes.find(t => t.id === value);
    setSelectedType(type);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Leave type — custom expandable list */}
      <InlineTypePicker
        leaveTypes={leaveTypes}
        selected={formData.leave_type}
        onSelect={handleTypeChange}
        selectedType={selectedType}
      />

      {/* Date de début + pill toggle */}
      <div className="space-y-2">
        <InlineDatePicker
          label="Date de début *"
          selected={formData.start_date}
          onSelect={(date) => setFormData(prev => ({ ...prev, start_date: date, end_date: date }))}
          holidays={holidays}
          testId="start-date-picker"
        />
        <HalfDayToggle
          checked={formData.half_day_start}
          onChange={(checked) => setFormData(prev => ({ ...prev, half_day_start: checked }))}
        />
      </div>

      {/* Date de fin + pill toggle */}
      <div className="space-y-2">
        <InlineDatePicker
          label="Date de fin *"
          selected={formData.end_date}
          onSelect={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
          disabled={(date) => formData.start_date && date < formData.start_date}
          holidays={holidays}
          testId="end-date-picker"
        />
        <HalfDayToggle
          checked={formData.half_day_end}
          onChange={(checked) => setFormData(prev => ({ ...prev, half_day_end: checked }))}
        />
      </div>

      {/* Days count */}
      {daysCount > 0 && (
        <div className={`p-3 rounded-lg ${isOverLimit ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${isOverLimit ? 'text-red-700' : 'text-gray-700'}`}>Durée de la demande :</span>
            <div className="flex items-center gap-2">
              <span className={`text-lg font-bold ${isOverLimit ? 'text-red-600' : 'text-[#00A3E0]'}`}>{daysCount} jour{daysCount > 1 ? 's' : ''}</span>
              {isOverLimit && (
                <div className="flex items-center gap-1 text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-xs">Max: {selectedType.max_days}j</span>
                </div>
              )}
            </div>
          </div>
          {excludedHolidays.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="flex items-center gap-1 text-amber-600 text-xs">
                <Flag className="h-3 w-3" />
                <span className="font-medium">Jours fériés exclus :</span>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {excludedHolidays.map((h, i) => (
                  <span key={i}>
                    {format(parseISO(h.date), "dd/MM", { locale: fr })} ({h.name})
                    {i < excludedHolidays.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Colleagues on leave */}
      {conflictingLeaves.length > 0 && (
        <div className="p-3 rounded-lg bg-orange-50 border border-orange-200">
          <div className="flex items-center gap-2 text-orange-700 mb-3">
            <Users className="h-4 w-4" />
            <span className="text-sm font-medium">Collègues absents sur cette période ({conflictingLeaves.length})</span>
          </div>
          <div className="bg-white rounded-lg p-3 border border-orange-100">
            <MiniAbsenceTimeline startDate={formData.start_date} endDate={formData.end_date} conflicts={conflictingLeaves} />
          </div>
          <p className="mt-3 text-xs text-orange-600 italic flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Pensez à vérifier l'organisation du service avant de soumettre
          </p>
        </div>
      )}

      {/* Comment */}
      <div className="space-y-2">
        <Label>Commentaire {selectedType?.id === 'autre' ? '*' : '(optionnel)'}</Label>
        <Textarea
          placeholder={selectedType?.id === 'autre' ? "Précisez le motif de votre demande..." : "Précisions sur votre demande..."}
          value={formData.comment}
          onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
          rows={3}
          data-testid="leave-comment"
        />
      </div>

      {/* Submit */}
      <div className="flex justify-end gap-3 pt-4 pb-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" className="bg-[#00A3E0] hover:bg-[#0090c7] text-white" disabled={loading || isOverLimit} data-testid="submit-leave-request">
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Envoi...
            </div>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Envoyer
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export const LeaveRequestForm = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [colleaguesLeaves, setColleaguesLeaves] = useState([]);
  const [conflictingLeaves, setConflictingLeaves] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [daysCount, setDaysCount] = useState(0);
  const [excludedHolidays, setExcludedHolidays] = useState([]);
  const [formData, setFormData] = useState({
    leave_type: "",
    start_date: null,
    end_date: null,
    comment: "",
    half_day_start: false,
    half_day_end: false
  });

  const { getAuthHeader } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const [typesRes, holidays2025, holidays2026, colleaguesRes] = await Promise.all([
        axios.get(`${API}/leave-types`, headers),
        axios.get(`${API}/holidays?year=2025`, headers).catch(() => ({ data: [] })),
        axios.get(`${API}/holidays?year=2026`, headers).catch(() => ({ data: [] })),
        axios.get(`${API}/leave-requests/colleagues`, headers).catch(() => ({ data: [] }))
      ]);
      const filteredTypes = typesRes.data.filter(t => t.id !== 'rtt');
      setLeaveTypes(filteredTypes);
      const allHolidays = [...(holidays2025.data || []), ...(holidays2026.data || [])];
      setHolidays(allHolidays.filter(h => h.active !== false));
      setColleaguesLeaves(colleaguesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [getAuthHeader]);

  // Fetch data when form opens
  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (formData.start_date && formData.end_date && colleaguesLeaves.length > 0) {
      const startDate = formData.start_date;
      const endDate = formData.end_date;
      const conflicts = colleaguesLeaves.filter(leave => {
        const leaveStart = parseISO(leave.start_date);
        const leaveEnd = parseISO(leave.end_date);
        return (
          isWithinInterval(startDate, { start: leaveStart, end: leaveEnd }) ||
          isWithinInterval(endDate, { start: leaveStart, end: leaveEnd }) ||
          isWithinInterval(leaveStart, { start: startDate, end: endDate }) ||
          isWithinInterval(leaveEnd, { start: startDate, end: endDate })
        );
      });
      setConflictingLeaves(conflicts);
    } else {
      setConflictingLeaves([]);
    }
  }, [formData.start_date, formData.end_date, colleaguesLeaves]);

  const calculateDays = useCallback(async () => {
    if (formData.start_date && formData.end_date) {
      try {
        const response = await axios.post(`${API}/calculate-days`, {
          leave_type: formData.leave_type || "cp",
          start_date: format(formData.start_date, "yyyy-MM-dd"),
          end_date: format(formData.end_date, "yyyy-MM-dd"),
          half_day_start: formData.half_day_start,
          half_day_end: formData.half_day_end
        }, getAuthHeader());
        setDaysCount(response.data.days_count);
        setExcludedHolidays(response.data.excluded_holidays || []);
      } catch {
        let days = 0;
        let current = new Date(formData.start_date);
        const end = new Date(formData.end_date);
        while (current <= end) {
          if (current.getDay() !== 0 && current.getDay() !== 6) days++;
          current = addDays(current, 1);
        }
        if (formData.half_day_start) days -= 0.5;
        if (formData.half_day_end) days -= 0.5;
        setDaysCount(Math.max(0.5, days));
        setExcludedHolidays([]);
      }
    } else {
      setDaysCount(0);
      setExcludedHolidays([]);
    }
  }, [formData.start_date, formData.end_date, formData.half_day_start, formData.half_day_end, formData.leave_type]);

  useEffect(() => {
    calculateDays();
  }, [calculateDays]);

  const resetForm = () => {
    setFormData({ leave_type: "", start_date: null, end_date: null, comment: "", half_day_start: false, half_day_end: false });
    setSelectedType(null);
    setDaysCount(0);
    setExcludedHolidays([]);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.leave_type || !formData.start_date || !formData.end_date) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    if (formData.start_date > formData.end_date) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }
    if (selectedType?.max_days && daysCount > selectedType.max_days) {
      toast.error(`Durée maximale: ${selectedType.max_days} jours pour ${selectedType.name}`);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leave-requests`, {
        leave_type: formData.leave_type,
        start_date: format(formData.start_date, "yyyy-MM-dd"),
        end_date: format(formData.end_date, "yyyy-MM-dd"),
        comment: formData.comment,
        half_day_start: formData.half_day_start,
        half_day_end: formData.half_day_end
      }, getAuthHeader());
      toast.success("Demande de congé envoyée!");
      handleClose();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const isOverLimit = selectedType?.max_days && daysCount > selectedType.max_days;

  const triggerButton = (
    <Button onClick={() => setOpen(true)} className="bg-[#00A3E0] hover:bg-[#0090c7] text-white shadow-lg shadow-cyan-500/20" data-testid="new-leave-request-btn">
      <Plus className="h-4 w-4 mr-2" />
      <span className="hidden sm:inline">Nouvelle demande</span>
      <span className="sm:hidden">Demande</span>
    </Button>
  );

  const formContent = (
    <LeaveFormContent
      formData={formData}
      setFormData={setFormData}
      leaveTypes={leaveTypes}
      holidays={holidays}
      selectedType={selectedType}
      setSelectedType={setSelectedType}
      daysCount={daysCount}
      excludedHolidays={excludedHolidays}
      conflictingLeaves={conflictingLeaves}
      isOverLimit={isOverLimit}
      loading={loading}
      handleSubmit={handleSubmit}
      onClose={handleClose}
    />
  );

  // Pure CSS overlay — no Radix, no event capturing, native elements work on Safari/iOS
  return (
    <>
      {triggerButton}
      {open && createPortal(
        <div className="fixed inset-0 z-[9999]" style={{ isolation: 'isolate' }}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
          {/* Panel */}
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center">
            <div className="relative bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl max-h-[100dvh] sm:max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
                <h2 className="text-xl font-bold text-[#1a2634]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Demande de congé
                </h2>
                <button onClick={handleClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Fermer">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              {/* Scrollable form */}
              <div className="flex-1 overflow-y-auto px-5 py-4 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                {formContent}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
